/**
 * Compliance HQ (Atlas 3.0 §L3) — 6-tab Compliance Officer command centre.
 *
 *  Tab 1 · Scorecard          — regime compliance scores + GA readiness gate
 *  Tab 2 · RoPA Export        — GDPR Art.30 Record of Processing Activities
 *  Tab 3 · Event Gap Tracker  — G1–G5 required events vs. manifest coverage
 *  Tab 4 · Audit Log          — Tier 0+1 events from manifest as audit trail
 *  Tab 5 · Consent Analytics  — consent machine states + legal-basis coverage
 *  Tab 6 · Third-Party Register — external processors, transfer mechanisms
 */
import { useMemo, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  computeComplianceScores,
  THIRD_PARTY_REGISTER,
  COMPLIANCE_REGIMES,
} from '../state/compliance.js';
import { detectEventGaps } from '../state/gapDetector.js';
import { generateRopa, formatRopaMarkdown, formatRopaJson } from '../state/ropaGenerator.js';
import { buildLegalBasisReport } from '../state/legalBasisMapper.js';
import { classifyEventTaxonomy, TIER_CONFIGS } from '../state/eventTaxonomy.js';
import { FSM_REGISTRY } from '../state/fsmRegistry.js';
import { navigate } from '../state/hashRouter.js';
import { Icon } from '../components/Icon.js';
import { ViewHeader } from '../components/ViewHeader.js';
import { TabBar } from '../components/TabBar.js';
import { DomainBadge } from './DomainBadge.js';

export interface ComplianceHQViewProps {
  manifest: AtlasManifest;
  onNavigateEntity?: (entityId: string) => void;
}

type TabId = 'scorecard' | 'ropa' | 'gaps' | 'audit' | 'consent' | 'thirdparty';

const TABS = [
  { id: 'scorecard', label: 'Scorecard', icon: 'verified_user' },
  { id: 'ropa', label: 'RoPA Export', icon: 'article' },
  { id: 'gaps', label: 'Event Gaps', icon: 'warning' },
  { id: 'audit', label: 'Audit Log', icon: 'receipt_long' },
  { id: 'consent', label: 'Consent', icon: 'handshake' },
  { id: 'thirdparty', label: 'Third Parties', icon: 'hub' },
] as const;

// ─── Scorecard tab ─────────────────────────────────────────────────────────────

function ScorecardTab({ manifest }: { manifest: AtlasManifest }): React.JSX.Element {
  const scores = useMemo(() => computeComplianceScores(manifest), [manifest]);
  const gapReport = useMemo(() => detectEventGaps(manifest), [manifest]);

  return (
    <div className="chq-scorecard">
      <div className="chq-ga-gate" data-ready={gapReport.gaReady}>
        <span className="material-symbols-rounded">
          {gapReport.gaReady ? 'check_circle' : 'cancel'}
        </span>
        <div>
          <strong>GA Readiness Gate</strong>
          <p>
            {gapReport.gaReady
              ? 'All G1–G5 events are in manifest + code. GA is unblocked.'
              : `${gapReport.missingFromManifest.length} required compliance event(s) missing. Resolve before GA.`}
          </p>
        </div>
        <div className="chq-completion">
          <span className="chq-pct">{gapReport.completionPct}%</span>
          <span className="chq-pct-label">gap coverage</span>
        </div>
      </div>

      <div className="chq-regime-grid">
        {scores.map((s) => {
          const regime = COMPLIANCE_REGIMES.find((r) => r.id === s.regimeId);
          const color =
            s.score >= 80 ? 'var(--good)' : s.score >= 50 ? 'var(--warn)' : 'var(--danger)';
          return (
            <div key={s.regimeId} className="chq-regime-card">
              <div className="chq-regime-header">
                <span className="chq-regime-label">{s.label.split('·')[0]?.trim()}</span>
                <span className="chq-regime-score" style={{ color }}>
                  {s.score}%
                </span>
              </div>
              <div className="chq-regime-bar">
                <div
                  className="chq-regime-fill"
                  style={{ width: `${s.score}%`, background: `linear-gradient(90deg, ${color}55, ${color})` }}
                />
              </div>
              <p className="chq-regime-detail">
                {s.entityCount} entit{s.entityCount === 1 ? 'y' : 'ies'} in scope
                {s.unmappedCount > 0 && (
                  <span className="chq-unmapped"> · {s.unmappedCount} without legal basis</span>
                )}
              </p>
              {regime?.articles && (
                <p className="chq-articles">{regime.articles.join(', ')}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RoPA tab ─────────────────────────────────────────────────────────────────

function RopaTab({ manifest }: { manifest: AtlasManifest }): React.JSX.Element {
  const records = useMemo(() => generateRopa(manifest), [manifest]);
  const [format, setFormat] = useState<'table' | 'markdown' | 'json'>('table');

  function download(text: string, filename: string, mime: string): void {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="chq-ropa">
      <div className="chq-toolbar">
        <div className="chq-format-pills">
          {(['table', 'markdown', 'json'] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`chq-pill${format === f ? ' chq-pill--active' : ''}`}
              onClick={() => setFormat(f)}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="chq-dl-btns">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => download(formatRopaMarkdown(records, manifest), 'ropa.md', 'text/markdown')}
          >
            <Icon name="download" /> .md
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => download(formatRopaJson(records), 'ropa.json', 'application/json')}
          >
            <Icon name="download" /> .json
          </button>
        </div>
      </div>

      {format === 'table' && (
        <div className="chq-table-wrap">
          <table className="chq-table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>Domain</th>
                <th>Department</th>
                <th>Lawful Basis</th>
                <th>Retention</th>
                <th>DPIA</th>
                <th>Data Categories</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.activityId}>
                  <td>
                    <strong>{r.activityName}</strong>
                  </td>
                  <td>
                    <DomainBadge domainId={r.domain} size="sm" />
                  </td>
                  <td>{r.department}</td>
                  <td>
                    {r.legalBasis ? (
                      <span className="chq-basis-chip">{r.legalBasis}</span>
                    ) : (
                      <span className="chq-warn-chip">⚠ unset</span>
                    )}
                  </td>
                  <td className="chq-retention">{r.retentionPeriod}</td>
                  <td>{r.dpiaRequired ? <span className="chq-dpia-chip">Required</span> : '—'}</td>
                  <td>
                    {r.dataCategories.map((dc) => (
                      <span
                        key={dc.label}
                        className={`chq-cat-chip${dc.isSpecialCategory ? ' chq-cat-chip--special' : ''}`}
                      >
                        {dc.label}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {format === 'markdown' && (
        <pre className="chq-export-pre">{formatRopaMarkdown(records, manifest)}</pre>
      )}
      {format === 'json' && (
        <pre className="chq-export-pre">{formatRopaJson(records)}</pre>
      )}
    </div>
  );
}

// ─── Event Gap tab ─────────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'var(--danger)',
  high: 'var(--warn)',
  medium: 'var(--info)',
  low: 'var(--text3)',
};

function GapsTab({ manifest }: { manifest: AtlasManifest }): React.JSX.Element {
  const report = useMemo(() => detectEventGaps(manifest), [manifest]);

  return (
    <div className="chq-gaps">
      <div className="chq-gaps-summary">
        <div className="chq-stat">
          <span className="chq-stat-n">{report.resolved.length}</span>
          <span className="chq-stat-l" style={{ color: 'var(--good)' }}>Resolved</span>
        </div>
        <div className="chq-stat">
          <span className="chq-stat-n">{report.uncoveredInCode.length}</span>
          <span className="chq-stat-l" style={{ color: 'var(--warn)' }}>In manifest, no code</span>
        </div>
        <div className="chq-stat">
          <span className="chq-stat-n">{report.missingFromManifest.length}</span>
          <span className="chq-stat-l" style={{ color: 'var(--danger)' }}>Missing from manifest</span>
        </div>
        <div className="chq-stat">
          <span className="chq-stat-n">{report.completionPct}%</span>
          <span className="chq-stat-l">Coverage</span>
        </div>
      </div>

      <div className="chq-finding-list">
        {report.findings.map((f) => (
          <div
            key={f.id}
            className="chq-finding"
            data-status={f.inManifest && f.hasCoverage ? 'ok' : f.inManifest ? 'partial' : 'missing'}
          >
            <div className="chq-finding-header">
              <span
                className="chq-finding-badge"
                style={{ background: SEVERITY_COLOR[f.severity] ?? 'var(--text3)' }}
              >
                {f.id}
              </span>
              <code className="chq-event-type">{f.requiredEventType}</code>
              <span className="chq-compliance-ref">{f.complianceRef}</span>
              <span className="chq-status-dot">
                {f.inManifest && f.hasCoverage ? (
                  <span className="material-symbols-rounded" style={{ color: 'var(--good)' }}>check_circle</span>
                ) : f.inManifest ? (
                  <span className="material-symbols-rounded" style={{ color: 'var(--warn)' }}>pending</span>
                ) : (
                  <span className="material-symbols-rounded" style={{ color: 'var(--danger)' }}>cancel</span>
                )}
              </span>
            </div>
            <p className="chq-finding-desc">{f.description}</p>
            {f.emittedBy.length > 0 && (
              <p className="chq-finding-emitters">
                Emitted by:{' '}
                {f.emittedBy.map((fn) => (
                  <button
                    key={fn}
                    type="button"
                    className="chq-fn-link"
                    onClick={() => navigate({ kind: 'cloud-function', id: fn })}
                  >
                    <code>{fn}</code>
                  </button>
                ))}
              </p>
            )}
            {f.remediation.length > 0 && (
              <details className="chq-remediation">
                <summary>Remediation steps</summary>
                <ol>
                  {f.remediation.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Audit Log tab ─────────────────────────────────────────────────────────────

function AuditTab({ manifest }: { manifest: AtlasManifest }): React.JSX.Element {
  const taxonomy = useMemo(() => classifyEventTaxonomy(manifest), [manifest]);
  const auditEvents = useMemo(
    () => taxonomy.enriched.filter((e) => e.resolvedTier === 0 || e.resolvedTier === 1),
    [taxonomy],
  );

  return (
    <div className="chq-audit">
      <p className="chq-audit-info">
        Showing {auditEvents.length} Tier 0 (Critical Safety) and Tier 1 (Compliance) events
        from the manifest — these require dual-write to the audit log.
      </p>
      <div className="chq-table-wrap">
        <table className="chq-table">
          <thead>
            <tr>
              <th>Event Type</th>
              <th>Tier</th>
              <th>PII Payload</th>
              <th>Dual-Write</th>
              <th>Compliance Ref</th>
              <th>Emitted By</th>
              <th>Storage Targets</th>
            </tr>
          </thead>
          <tbody>
            {auditEvents.map((e) => (
              <tr key={e.eventType.type}>
                <td>
                  <button
                    type="button"
                    className="chq-event-type-btn"
                    onClick={() => navigate({ kind: 'event-type', id: e.eventType.type })}
                  >
                    <code>{e.eventType.type}</code>
                  </button>
                </td>
                <td>
                  <span
                    className="chq-tier-chip"
                    style={{ background: e.tierConfig.color }}
                  >
                    T{e.resolvedTier} · {e.tierConfig.label}
                  </span>
                </td>
                <td>
                  {e.hasPiiPayload ? (
                    <span className="chq-pii-chip">
                      {e.piiPayloadFields.join(', ')}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {e.tierConfig.requiresDualWrite ? (
                    <span className="material-symbols-rounded" style={{ color: 'var(--good)' }}>check</span>
                  ) : '—'}
                </td>
                <td>
                  {e.eventType.complianceRef ? (
                    <code>{e.eventType.complianceRef}</code>
                  ) : (
                    <span className="chq-warn-chip">⚠ unset</span>
                  )}
                </td>
                <td>
                  {e.emittedBy.length > 0
                    ? e.emittedBy.map((fn) => (
                        <button
                          key={fn}
                          type="button"
                          className="chq-fn-chip chq-fn-chip-btn"
                          onClick={() => navigate({ kind: 'cloud-function', id: fn })}
                        >
                          {fn}
                        </button>
                      ))
                    : <span className="chq-warn-chip">⚠ uncovered</span>}
                </td>
                <td className="chq-targets">{e.tierConfig.storageTargets.join(' · ')}</td>
              </tr>
            ))}
            {auditEvents.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)' }}>
                  No Tier 0/1 events found in manifest.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="chq-tier-legend">
        {TIER_CONFIGS.map((t) => (
          <div key={t.tier} className="chq-tier-legend-item">
            <span className="chq-tier-dot" style={{ background: t.color }} />
            <strong>T{t.tier} · {t.label}</strong>
            <span>{t.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Consent tab ──────────────────────────────────────────────────────────────

function ConsentTab({ manifest }: { manifest: AtlasManifest }): React.JSX.Element {
  const legalBasisReport = useMemo(() => buildLegalBasisReport(manifest), [manifest]);
  const consentFsm = FSM_REGISTRY.find((m) => m.id === 'consentMachine');

  const basisColors: Record<string, string> = {
    consent: '#8B5CF6',
    contract: '#3B82F6',
    legal_obligation: '#EF4444',
    vital_interests: '#EC4899',
    public_task: '#6366F1',
    legitimate_interests: '#F59E0B',
  };

  return (
    <div className="chq-consent">
      <div className="chq-consent-header">
        <div className="chq-stat">
          <span className="chq-stat-n">{legalBasisReport.mappings.length}</span>
          <span className="chq-stat-l">PII Entities</span>
        </div>
        <div className="chq-stat">
          <span className="chq-stat-n" style={{ color: 'var(--good)' }}>
            {legalBasisReport.coveragePct}%
          </span>
          <span className="chq-stat-l">Legal Basis Coverage</span>
        </div>
        <div className="chq-stat">
          <span className="chq-stat-n" style={{ color: legalBasisReport.gaps.length > 0 ? 'var(--danger)' : 'var(--good)' }}>
            {legalBasisReport.gaps.length}
          </span>
          <span className="chq-stat-l">Unmapped Gaps</span>
        </div>
      </div>

      <h3 className="chq-section-title">Legal Basis Distribution</h3>
      <div className="chq-basis-distribution">
        {(Object.entries(legalBasisReport.byBasis) as [string, string[]][])
          .filter(([, ids]) => ids.length > 0)
          .sort(([, a], [, b]) => b.length - a.length)
          .map(([basis, ids]) => (
            <div key={basis} className="chq-basis-row">
              <span
                className="chq-basis-swatch"
                style={{ background: basisColors[basis] ?? 'var(--text3)' }}
              />
              <span className="chq-basis-name">{basis.replace('_', ' ')}</span>
              <div className="chq-basis-bar-wrap">
                <div
                  className="chq-basis-bar"
                  style={{
                    width: `${Math.round((ids.length / legalBasisReport.mappings.length) * 100)}%`,
                    background: basisColors[basis] ?? 'var(--text3)',
                  }}
                />
              </div>
              <span className="chq-basis-count">{ids.length}</span>
            </div>
          ))}
      </div>

      {legalBasisReport.gaps.length > 0 && (
        <>
          <h3 className="chq-section-title chq-warn">
            <span className="material-symbols-rounded">warning</span>
            {legalBasisReport.gaps.length} unmapped entit{legalBasisReport.gaps.length === 1 ? 'y' : 'ies'}
          </h3>
          <div className="chq-table-wrap">
            <table className="chq-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Domain</th>
                  <th>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {legalBasisReport.gaps.map((g) => (
                  <tr key={g.entityId}>
                    <td><code>{g.entityId}</code></td>
                    <td><DomainBadge domainId={g.domain} size="sm" /></td>
                    <td>{g.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {consentFsm && (
        <>
          <h3 className="chq-section-title">Consent FSM States</h3>
          <div className="chq-fsm-states">
            {consentFsm.states.map((s) => (
              <div key={s.id} className="chq-fsm-state" data-terminal={s.isFinal}>
                <span className="chq-fsm-state-id">{s.id}</span>
                <span className="chq-fsm-state-label">{s.label}</span>
                {consentFsm.invariants.filter((inv) => inv.applyInStates.includes(s.id)).length > 0 && (
                  <ul className="chq-fsm-invariants">
                    {consentFsm.invariants
                      .filter((inv) => inv.applyInStates.includes(s.id))
                      .map((inv, i) => <li key={i}>{inv.description}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Third-party tab ──────────────────────────────────────────────────────────

function ThirdPartyTab(): React.JSX.Element {
  return (
    <div className="chq-thirdparty">
      <p className="chq-audit-info">
        {THIRD_PARTY_REGISTER.length} registered data processors.
        All transfers use Standard Contractual Clauses (SCCs) under GDPR Art.46.
      </p>
      <div className="chq-table-wrap">
        <table className="chq-table">
          <thead>
            <tr>
              <th>Processor</th>
              <th>Role</th>
              <th>Country</th>
              <th>Data Categories</th>
              <th>Domains</th>
              <th>Transfer Mechanism</th>
            </tr>
          </thead>
          <tbody>
            {THIRD_PARTY_REGISTER.map((tp) => (
              <tr key={tp.name}>
                <td><strong>{tp.name}</strong></td>
                <td>
                  <span className={`chq-role-chip chq-role-chip--${tp.role}`}>
                    {tp.role}
                  </span>
                </td>
                <td>{tp.country ?? '—'}</td>
                <td>
                  {tp.dataCategories.map((dc) => (
                    <span key={dc} className="chq-cat-chip">{dc}</span>
                  ))}
                </td>
                <td>
                  {tp.domains.map((d) => <DomainBadge key={d} domainId={d} size="sm" />)}
                </td>
                <td>
                  {tp.transferMechanism ? (
                    <span className="chq-mech-chip">{tp.transferMechanism}</span>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────

export function ComplianceHQView({
  manifest,
}: ComplianceHQViewProps): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<TabId>('scorecard');

  return (
    <div className="chq-root">
      <ViewHeader
        eyebrow="COMPLIANCE HQ"
        title="Compliance Command Centre"
        subtitle="GDPR · CCPA · KOSA · COPPA · CFPB 1033"
      />
      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="chq-body" role="tabpanel" aria-live="polite">
        {activeTab === 'scorecard' && <ScorecardTab manifest={manifest} />}
        {activeTab === 'ropa' && <RopaTab manifest={manifest} />}
        {activeTab === 'gaps' && <GapsTab manifest={manifest} />}
        {activeTab === 'audit' && <AuditTab manifest={manifest} />}
        {activeTab === 'consent' && <ConsentTab manifest={manifest} />}
        {activeTab === 'thirdparty' && <ThirdPartyTab />}
      </div>
    </div>
  );
}
